import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../components/section/SectionHeader";
import {
  ORGANIZATION_FILTER_COLUMNS,
  ORGANIZATION_TABLE_HEADERS,
  EDIT_ORGANIZATION_FORM_FIELDS,
  ADD_ORGANIZATION_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformOrgProfileUpdateBody,
  transformOrganizationProfileResponseRow,
  transformOrganizationResponseRow,
  transformOrganizationRows,
  transformOrganizationUpdateBody,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteOrganization,
  getOrganizationByRef,
  getOrganizationProfileByRef,
  getOrganizationandProfileDetailsByRef,
  updateOrganizationByRef,
  updateOrganizationProfileByOrgID,
} from "../api";
import {
  INITIAL_ORGANIZATION_EDIT_STATE,
  INITIAL_ORGANIZATION_PROFILE_EDIT_STATE,
} from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_ORG_TRANSLATIONS,
  READ_ORG_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import EditOrg from "../components/dataTable/EditOrgProfile";
import { EDIT_ORGANIZATION_PROFILE_FORM_FIELDS } from "../constants/editFormFields";
import BulkUpload from "../components/bulkUpload/BulkUpload";
import OrgContext from "../store/orgContext";

interface ISearchData {
  "Organisation ID"?: string | null;
  "Organisation Name"?: string | null;
}

const INITIAL_STATE = {
  "Organisation ID": "",
  "Organisation Name": "",
};

const searchFields = ["Organisation ID", "Organisation Name"];
const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_ORGANIZATION_FORM_FIELDS
);

let searchValue: string | null | undefined = null;

const Organizations = () => {
  const {
    token,
    scopes: { orgs: org, org_profile: orgProfile },
  } = useContext(AuthContext);
  const { setOrgs, selectedOrg, updateOrg } = useContext(OrgContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle("Organisations- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [orgData, setOrgData] = useState<any[]>([]);

  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const [selectedColumn, setSelectedColumn] = useState<{
    id: string;
    value: string;
  } | null>(null);

  const fetchOrganizations = async (isDeleted?: boolean) => {
    setLoading(true);
    try {
      const { data, pageNum, pageSize, sortByField, sortDirection } =
        searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
        ...(sortByField && { sortByField }),
        ...(sortDirection && { sortDirection }),
        ...(data &&
          data["Organisation ID"] && {
            "ORG-ID": data["Organisation ID"],
          }),
        ...(data &&
          data["Organisation Name"] && {
            "ORG-NAME": data["Organisation Name"],
          }),
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/organisations?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setOrgData([...response.Rows]);
      // update organisation list after deleting an organisation
      if (isDeleted) {
        const tranformedOrgRows = response.Rows.map((row) => ({
          id: row["ORG-ID"],
          text: `${row["ORG-NAME"]} (${row["ORG-ID"]})`,
        }));
        setOrgs(tranformedOrgRows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const deleteConnection = async (ref: string) => {
    if (token) {
      try {
        await deleteOrganization({
          token,
          ref,
        });
        successAlert(DELETE_ORG_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchOrganizations(true);
        // if selected org is deleted
        if (selectedOrg && selectedOrg.id === ref) {
          updateOrg("");
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_ORG_TRANSLATIONS.header}
        pageDescription={READ_ORG_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            {/* <ESOganisationDropdown disabled /> */}
          </div>
        }
      />

      {showAdvancedSearch ? (
        <AdvancedSearch
          setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
          advancedSearchState={searchData}
          advancedSearchConfig={searchConfigNew}
          setAdvancedSearchState={setSearchData}
          onSubmit={(data) => {
            const currentSearchData = searchQuery.data;
            setSearhQuery({
              ...searchQuery,
              pageNum: 1,
              pageSize: 20,
              data: { ...currentSearchData, ...data },
            });
          }}
          onCancel={() => {
            setSearchData(INITIAL_STATE);
            handleToggleAdvanceSearchPanel();
          }}
        />
      ) : null}

      {showSidePanel && selectedConnectionId && !isBulkUploadEnabled ? (
        <EditOrg
          panelName={READ_ORG_TRANSLATIONS.editRow.panelName}
          initialOrgState={INITIAL_ORGANIZATION_EDIT_STATE}
          initialOrgProfileState={INITIAL_ORGANIZATION_PROFILE_EDIT_STATE}
          esRef={selectedConnectionId}
          setShowEditPanel={handleToggleSidePanel}
          onDelete={() => deleteConnection(selectedConnectionId)}
          orgFormFields={EDIT_ORGANIZATION_FORM_FIELDS}
          orgProfileFormFields={EDIT_ORGANIZATION_PROFILE_FORM_FIELDS}
          header={READ_ORG_TRANSLATIONS.editRow.header}
          showSmallSidepanel
          transformResponseRow={transformOrganizationResponseRow}
          transformProfileResponseRow={transformOrganizationProfileResponseRow}
          transformOrgUpdateBody={transformOrganizationUpdateBody}
          transformOrgProfileUpdateBody={transformOrgProfileUpdateBody}
          onOrgUpdate={updateOrganizationByRef}
          onOrgProfileUpdate={updateOrganizationProfileByOrgID}
          loadDataByRef={getOrganizationandProfileDetailsByRef}
          fetchOrgProfileData={getOrganizationProfileByRef}
          fetchOrgData={getOrganizationByRef}
          hasDeletePermission={org && org.includes("d")}
          hasOrgEditPermission={org && org.includes("u")}
          hasOrgProfileEditPermission={orgProfile && orgProfile.includes("u")}
          hasOrgProfileViewPermission={orgProfile && orgProfile.includes("r")}
          refreshTable={fetchOrganizations}
          deleteModalItemName="ORG-ID"
        />
      ) : null}

      {showSidePanel && isBulkUploadEnabled ? (
        <BulkUpload
          moduleName="Organisation"
          pathName="/api/v1/organisations"
          csvTemplateFile={["ORGANISATION-ID,ORGANISATION-NAME"]}
          closeBulkUploadSidePanel={() => {
            handleToggleSidePanel();
            setIsBulkUploadEnabled(false);
            fetchOrganizations();
          }}
        />
      ) : null}

      <DataTableWithControls
        loading={loading}
        filterColumns={ORGANIZATION_FILTER_COLUMNS}
        selectedColumn={selectedColumn}
        setSelectedColumn={setSelectedColumn}
        onSearch={(term) => {
          searchValue = term;
          setSearhQuery({
            ...searchQuery,
            pageNum: 1,
            pageSize: 20,
            data: term
              ? { ...searchData, [`${selectedColumn?.id}`]: term }
              : { ...searchData },
          });
        }}
        setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
        showCreateButton={org && org.includes("c")}
        onCreateButtonClick={() => {
          navigate(`/configurationManagement/organizations/new`);
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows={transformOrganizationRows(orgData).map((row: any) => ({
          ...row,
          action: (
            <TableActionButton
              onActionButtonClick={() => {
                setSelectedConnectionId(row.id);
                handleToggleSidePanel();
              }}
            />
          ),
        }))}
        numberOfAdvancedSearchFilters={searchConfigNew.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc, el: any) => {
            const { name } = el;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj: any = { ...searchData };
            return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
          },
          0
        )}
        clearAdvancedSearchFilters={() => {
          setSearchData(INITIAL_STATE);
          setSearhQuery({
            ...searchQuery,
            pageNum: 1,
            pageSize: 20,
            data:
              selectedColumn && searchValue
                ? { [`${selectedColumn.id}`]: searchValue }
                : {},
          });
        }}
        headers={ORGANIZATION_TABLE_HEADERS}
        pageNum={searchQuery.pageNum}
        setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
        totalItems={numberOfRows}
        totalPages={numberOfPages}
        pageSize={searchQuery.pageSize}
        setPageSize={(pageSize) => setSearhQuery({ ...searchQuery, pageSize })}
        sortByField={searchQuery.sortByField}
        sortDirection={searchQuery.sortDirection}
        handleSort={sortTable}
        showBulkUploadButton={org && org.includes("c")}
        onBulkUploadButtonClick={() => {
          handleToggleSidePanel();
          setIsBulkUploadEnabled(true);
        }}
      />
    </>
  );
};

export default Organizations;
